import { useEffect } from 'react'
import type { SupplierAnalysis } from '../../hooks/useAiInsights'
import { AI_LABEL } from '../../lib/statusStyles'

interface Props {
  supplierName: string
  /** Total de itens no pacote do fornecedor (não só os destaques). */
  totalItems: number
  /** Subtotal estimado do pacote em BRL. */
  subtotalBRL: number
  leadTimeDays: number | null | undefined
  analysis: SupplierAnalysis
  /** Aplica as qty da composicao_extra na coluna Qtd do pai. Recebe a lista
   *  toda; pai resolve sku→id e decide o que fazer com itens fora do filtro. */
  onApplyComposicao?: (items: NonNullable<SupplierAnalysis['composicao_extra']>) => void
  onClose: () => void
}

const VEREDITO_CONFIG = {
  aprovar: {
    label: '✅ APROVAR',
    title: 'Pacote sólido',
    bg: 'bg-emerald-900/40',
    border: 'border-emerald-500/60',
    text: 'text-emerald-300',
    glow: 'shadow-emerald-500/20',
  },
  aprovar_com_cortes: {
    label: '⚠️ APROVAR COM CORTES',
    title: 'Aprovar parcialmente',
    bg: 'bg-amber-900/40',
    border: 'border-amber-500/60',
    text: 'text-amber-300',
    glow: 'shadow-amber-500/20',
  },
  postergar: {
    label: '⏸️ POSTERGAR',
    title: 'Não comprar agora',
    bg: 'bg-rose-900/40',
    border: 'border-rose-500/60',
    text: 'text-rose-300',
    glow: 'shadow-rose-500/20',
  },
  completar_pedido: {
    label: '🛒 FALTA FECHAR MÍNIMO',
    title: 'Adicionar itens',
    bg: 'bg-orange-900/40',
    border: 'border-orange-500/60',
    text: 'text-orange-300',
    glow: 'shadow-orange-500/20',
  },
} as const

const TAG_CONFIG = {
  URGENTE: { bg: 'bg-red-600', text: 'text-white', icon: '🚨' },
  REPOR: { bg: 'bg-amber-500', text: 'text-gray-900', icon: '⚡' },
  OPORTUNIDADE: { bg: 'bg-brand-600', text: 'text-white', icon: '💎' },
} as const

const CURVA_CONFIG = {
  'A': { bg: 'bg-emerald-700', text: 'text-white', label: 'A' },
  'B': { bg: 'bg-amber-600', text: 'text-white', label: 'B' },
  'C': { bg: 'bg-rose-700', text: 'text-white', label: 'C' },
  '?': { bg: 'bg-sunken', text: 'text-ink-muted', label: '?' },
} as const

const fmtBRL = (n: number) =>
  n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })

export default function SupplierAnalysisModal({
  supplierName,
  totalItems,
  subtotalBRL,
  leadTimeDays,
  analysis,
  onApplyComposicao,
  onClose,
}: Props) {
  useEffect(() => {
    function onEsc(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onEsc)
    // Trava scroll do body enquanto o modal está aberto.
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onEsc)
      document.body.style.overflow = prevOverflow
    }
  }, [onClose])

  const vere = VEREDITO_CONFIG[analysis.veredito] ?? VEREDITO_CONFIG.aprovar_com_cortes

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className={`relative w-full max-w-4xl bg-gradient-to-br from-surface via-surface to-brand-100/50 dark:to-brand-950 border-2 ${vere.border} rounded-2xl shadow-2xl ${vere.glow} overflow-hidden max-h-[90dvh] overflow-y-auto overscroll-contain`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header — fornecedor + close (sticky pra ✕ ficar sempre acessível) */}
        <div className="sticky top-0 z-10 bg-surface/95 backdrop-blur-sm px-5 py-4 border-b border-line flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className={`flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold ${AI_LABEL}`}>
              <span>✨ Análise IA</span>
              <span className="text-ink-faint">·</span>
              <span className="text-ink-faint">Claude Haiku</span>
            </div>
            <h2 className="text-ink text-lg font-bold mt-0.5 truncate" title={supplierName}>
              {supplierName}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 text-ink-faint hover:text-ink text-xl leading-none w-7 h-7 flex items-center justify-center rounded hover:bg-sunken"
            aria-label="Fechar"
          >
            ✕
          </button>
        </div>

        {/* Métricas em chip */}
        <div className="px-5 py-3 flex flex-wrap gap-2 border-b border-line">
          <Chip label="Itens" value={String(totalItems)} />
          <Chip label="Subtotal" value={fmtBRL(subtotalBRL)} />
          <Chip label="Lead time" value={leadTimeDays != null ? `${leadTimeDays}d` : '—'} />
        </div>

        {/* Veredito gigante */}
        <div className={`px-5 py-4 ${vere.bg} border-b ${vere.border}`}>
          <div className={`text-xs font-bold ${vere.text} tracking-widest`}>{vere.label}</div>
          <p className="text-sm text-ink mt-1.5 leading-snug">{analysis.resumo}</p>
        </div>

        {/* Compra mínima — barra de progresso */}
        {analysis.compra_minima?.minimo != null && (
          <CompraMinimaBar cm={analysis.compra_minima} />
        )}

        {/* Composição extra — itens pra fechar mínimo */}
        {analysis.composicao_extra && analysis.composicao_extra.length > 0 && (analysis.compra_minima?.gap ?? 0) > 0 && (
          <div className="px-5 py-4 bg-orange-950/15 border-b border-orange-700/30">
            <div className="flex items-center justify-between gap-2 mb-2.5">
              <h3 className="text-[11px] text-orange-300 uppercase tracking-wider font-bold flex items-center gap-2">
                📦 Adicionar pra fechar pedido
                <span className="text-ink-faint font-normal normal-case text-[10px]">— ordem curva A → B → C, sem encalhe</span>
              </h3>
              {onApplyComposicao && (
                <button
                  onClick={() => {
                    onApplyComposicao(analysis.composicao_extra!)
                    onClose()
                  }}
                  className="text-xs px-3 py-1.5 bg-orange-700 hover:bg-orange-600 text-white rounded-lg font-semibold transition-colors flex items-center gap-1.5"
                  title="Escreve as quantidades sugeridas na coluna Qtd da tabela"
                >
                  ✅ Aplicar quantidades
                </button>
              )}
            </div>
            <ul className="space-y-1.5">
              {analysis.composicao_extra.map((c, i) => {
                const curva = CURVA_CONFIG[c.curva] ?? CURVA_CONFIG['?']
                return (
                  <li
                    key={`${c.sku}-${i}`}
                    className="flex items-center gap-2.5 p-2 bg-surface rounded-md border border-line"
                  >
                    <span className={`shrink-0 ${curva.bg} ${curva.text} text-[10px] font-bold w-6 h-6 flex items-center justify-center rounded leading-none`}>
                      {curva.label}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-ink truncate flex items-center gap-1.5" title={c.name}>
                        {c.origem === 'aumento_carrinho' && (
                          <span
                            className="shrink-0 text-[9px] font-bold text-sky-300 bg-sky-900/40 border border-sky-700/40 px-1.5 py-0.5 rounded leading-none"
                            title="Item já no carrinho — qty aumentada pra fechar mínimo"
                          >
                            ↑ AUMENTO
                          </span>
                        )}
                        <span className="font-mono text-[10px] text-ink-faint">{c.sku}</span>
                        <span className="truncate">{c.name}</span>
                      </div>
                      <p className="text-[10px] text-ink-faint mt-0.5">
                        {c.origem === 'aumento_carrinho' && c.qty_atual != null ? (
                          <span className="text-orange-600 dark:text-orange-300 font-semibold">
                            qty <span className="text-ink-faint line-through">{c.qty_atual}</span> → {c.qty}
                          </span>
                        ) : (
                          <>
                            <span className="text-orange-600 dark:text-orange-300 font-semibold">qty {c.qty}</span>
                            {c.ajustada && (
                              <span
                                className="ml-1 text-[9px] text-ink-faint line-through"
                                title={`Motor sugeria ${c.qty_sugerida} — ajustado pra não passar do mínimo`}
                              >
                                (de {c.qty_sugerida})
                              </span>
                            )}
                          </>
                        )}
                        <span className="mx-1 text-ink-faint">·</span>
                        {c.razao}
                      </p>
                    </div>
                    <span className="shrink-0 text-sm text-orange-700 dark:text-orange-200 font-semibold tabular-nums">
                      {fmtBRL(c.valor)}
                    </span>
                  </li>
                )
              })}
            </ul>
          </div>
        )}

        {/* Prazos de pagamento (só após dia de corte) */}
        {analysis.apos_dia_25 && analysis.prazos_recomendados && analysis.prazos_recomendados.length > 0 && (
          <div className="px-5 py-3 bg-sky-950/20 border-b border-sky-700/30">
            <h3 className="text-[11px] text-sky-300 uppercase tracking-wider font-bold mb-2 flex items-center gap-2">
              💳 Prazos de pagamento
              <span className="text-ink-faint font-normal normal-case text-[10px]">— escolha sua condição</span>
            </h3>
            <div className="flex flex-wrap gap-1.5">
              {analysis.prazos_recomendados.map((t, i) => (
                <div
                  key={`${t.dias}-${i}`}
                  className={`px-2.5 py-1 rounded-md text-xs flex items-center gap-1.5 border ${
                    t.mes_seguinte
                      ? 'bg-sky-100 border-sky-300 text-sky-800 dark:bg-sky-500/15 dark:border-sky-500/30 dark:text-sky-300'
                      : 'bg-sunken border-line text-ink-muted'
                  }`}
                  title={t.mes_seguinte ? 'Vencimento cai no mês seguinte — alívio de caixa' : 'Vencimento no mês atual'}
                >
                  <span className="font-semibold tabular-nums">{t.dias}d</span>
                  <span className="text-ink-faint">→</span>
                  <span className="tabular-nums">{t.vencimento_label}</span>
                  {t.mes_seguinte && <span className="text-[9px]">✓</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Destaques */}
        {analysis.destaques?.length > 0 && (
          <div className="px-5 py-4">
            <h3 className="text-[11px] text-ink-muted uppercase tracking-wider font-semibold mb-2.5">
              🎯 Destaques do pacote
            </h3>
            <ul className="space-y-2">
              {analysis.destaques.map((d, i) => {
                const tag = TAG_CONFIG[d.tag] ?? TAG_CONFIG.REPOR
                return (
                  <li
                    key={`${d.sku}-${i}`}
                    className="flex items-start gap-2.5 p-2.5 bg-sunken rounded-lg border border-line hover:border-ink-faint transition-colors"
                  >
                    <span className={`shrink-0 ${tag.bg} ${tag.text} text-[10px] font-bold px-2 py-1 rounded inline-flex items-center gap-1 leading-none`}>
                      <span>{tag.icon}</span>
                      <span>{d.tag}</span>
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-ink font-medium truncate" title={d.name}>
                        <span className="font-mono text-xs text-ink-faint mr-1.5">{d.sku}</span>
                        {d.name}
                      </div>
                      <p className="text-xs text-ink-muted mt-0.5 leading-snug">{d.razao}</p>
                    </div>
                  </li>
                )
              })}
            </ul>
          </div>
        )}

        {/* Cortes (se houver) */}
        {analysis.cortes?.length > 0 && (
          <div className="px-5 pb-4">
            <h3 className="text-[11px] text-ink-muted uppercase tracking-wider font-semibold mb-2 mt-1">
              ✂️ Considere cortar
            </h3>
            <ul className="space-y-1.5">
              {analysis.cortes.map((c, i) => (
                <li
                  key={`${c.sku}-${i}`}
                  className="flex items-start gap-2 p-2 bg-danger/5 rounded-md border-l-2 border-rose-400"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-ink-muted truncate" title={c.name}>
                      <span className="font-mono text-[10px] text-ink-faint mr-1">{c.sku}</span>
                      {c.name}
                    </div>
                    <p className="text-[11px] text-danger mt-0.5">{c.razao}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Footer com botão de ação */}
        <div className="px-5 py-3 bg-sunken border-t border-line flex items-center justify-end">
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-surface hover:bg-line text-ink-muted text-xs rounded-lg transition-colors border border-line"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}

function Chip({ label, value }: { label: string; value: string }) {
  return (
    <div className="px-3 py-1.5 bg-sunken border border-line rounded-lg">
      <div className="text-[9px] text-ink-faint uppercase tracking-wider">{label}</div>
      <div className="text-sm text-ink font-semibold tabular-nums leading-tight">{value}</div>
    </div>
  )
}

interface CompraMinimaBarProps {
  cm: NonNullable<SupplierAnalysis['compra_minima']>
}

function CompraMinimaBar({ cm }: CompraMinimaBarProps) {
  const pct = Math.min(100, cm.percentual ?? 0)
  const cor = cm.atingida
    ? { bar: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-300', label: '✅ Compra mínima atingida' }
    : pct >= 80
      ? { bar: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-300', label: `Faltam ${fmtBRL(cm.gap)} para o mínimo` }
      : { bar: 'bg-rose-500', text: 'text-rose-600 dark:text-rose-300', label: `Faltam ${fmtBRL(cm.gap)} para o mínimo` }

  return (
    <div className="px-5 py-3 border-b border-line">
      <div className="flex items-center justify-between text-[11px] mb-1.5">
        <span className={`font-bold uppercase tracking-wider ${cor.text}`}>{cor.label}</span>
        <span className="text-ink-muted tabular-nums">
          <span className="text-ink font-semibold">{fmtBRL(cm.valor_atual)}</span>
          <span className="text-ink-faint mx-1">/</span>
          <span>{fmtBRL(cm.minimo ?? 0)}</span>
          {cm.percentual != null && <span className="ml-2 text-ink-faint">({cm.percentual}%)</span>}
        </span>
      </div>
      <div className="w-full h-2 bg-sunken rounded-full overflow-hidden">
        <div
          className={`h-full ${cor.bar} transition-all duration-500`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}
